<template>
  <div class="nwx-picklist"
       v-click-outside="hideDropdown"
  >
    <div :class="themeClass">
      <div :class="{
          'picklist-cont': true,
          'opened': isDropdownVisible,
          'autocomplete': autocomplete,
          'dense': dense,
          'focused': focused,
          'disabled': disabled,
          'error': error,
          'has-value': shouldHighlight,
        }"
           @click.stop="openDropdown()"
           @keyup.enter="openDropdown()"
      >
        <input
          :type="isMobile && !autocomplete ? 'null' : 'text'"
          :name="name"
          :autocomplete="inputAutocomplete"
          class="picklist-input"
          ref="input"
          :placeholder="placeholder"
          v-model="localDisplayed"
          :disabled="disabled"
          :readonly="(!disabled && !autocomplete)
                  || (!disabled && multiple)
                  || (isMobile && !autocomplete)"
          @blur="$emit('blur')"
        />
      </div>
      <div :class="{
          'picklist-dropdown': true,
          'visible': isDropdownVisible,
          'scrollable': isDropdownScrollable,
          'dense': dense,
        }"
           ref="dropdown"
      >
        <div class="no-option-msg" v-if="filteredItems.length === 0">
          Sorry, no options available
        </div>
        <template v-else>
          <div
            class="option-row disabled-row"
            v-if="disabledOptionText"
          >
            <label :class="{
                  'radio-container': true,
                  'checked': false
                }"
            >
              <slot name="option-text" :option="disabledOptionText">
                <div class="label-text" v-html="disabledOptionText"></div>
              </slot>
            </label>
          </div>
          <div
            class="option-row"
              v-if="selectAllText && multiple"
          >
            <label :class="{
                  'checkbox-container': true,
                  checked: this.isSelectAll,
                }"
                   :for="`checkbox-id-select-all-${uid}`"
            >
              <slot name="option-text" :option="selectAllText">
                <div class="label-text" v-html="selectAllText"></div>
              </slot>
              <input type="checkbox"
                     :name="`checkbox-id-all-${uid}`"
                     :id="`checkbox-id-select-all-${uid}`"
                     autocomplete="off"
                     :checked="isSelectAll"
                     @click.stop="toggleAllOptions"
              >
              <span class="checkmark"></span>
            </label>
          </div>

          <div
            class="option-row"
             :class="{
                'disabled-row': isDisabledChekbox(option),
              }"
             v-for="(option, index) in filteredItems"
             :key="index"
          >
            <!-- bundle -->
            <template v-if="option.type && option.type === 'bundle'">
              <label
                    class="checkbox-container"
                    :class="{
                      'checked': bundleIsChecked(option)
                    }"
                    v-if="multiple"
                    :for="multiple
                      ? `checkbox-child-id-${uid}${option.value}`
                      : `radio-id-${uid}${option.value}`"
                    @click="toggleBundleItems(option)"
                  >
                    <slot name="option-bundle-text" :option="option">
                      <div class="label-text" v-html="option.text"></div>
                    </slot>
                    <span class="checkmark"></span>
                  </label>
            </template>
            <!-- other items  -->
            <template v-else>
              <label :class="{
                  'checkbox-container': true,
                  'checked': localSelected && localSelected.indexOf(option.value) !== -1
                }"
                 v-if="multiple"
                 :for="multiple ? `checkbox-id-${uid}${index}` : `radio-id-${uid}${index}`"
              >
                <slot name="option-text" :option="option">
                  <div class="label-text" v-html="option.text"></div>
                </slot>
                <input type="checkbox"
                       :value="option.value"
                       :name="`checkbox-id-${uid}`"
                       :id="`checkbox-id-${uid}${index}`"
                       v-model="localSelected"
                       :disabled="isDisabledChekbox(option)"
                       autocomplete="off"
                       @change="changeHandler(option)"
                >
                <span class="checkmark"></span>
              </label>

              <label :class="{
                  'radio-container': true,
                  'checked': option.value === localSelected
                }"
                     v-else
              >
                <slot name="option-text" :option="option">
                  <div class="label-text" v-html="option.text"></div>
                </slot>
                <input type="radio"
                       :value="option.value"
                       :name="`radio-id-${uid}`"
                       :id="`radio-id-${uid}${index}`"
                       v-model="localSelected"
                       @click.stop="clickOption(option)"
                >
              </label>
            </template>
        </div>
        </template>
      </div>
  </div>
  </div>
</template>

<script>
  import ClickOutside from 'vue-click-outside';

  // Value, Text
  export default {
    name: 'NwxPicklist',
    directives: {
      ClickOutside,
    },
    props: {
      theme: {
        type: String,
        default: 'classic',
        validator: (themeVersion) => (['classic', 'pp-modern'].includes(themeVersion)),
      },
      name: {
        type: String,
        default: '',
      },
      inputAutocomplete: {
        type: String,
        default: undefined,
      },
      selected: {
        type: [String, Array],
        default: undefined,
      },
      items: {
        type: Array,
        default: () => [],
      },
      productGroups: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: '',
      },
      multiple: Boolean,
      autocomplete: Boolean,
      dense: Boolean,
      focused: Boolean,
      disabled: Boolean,
      limit: Number,
      error: Boolean,
      disabledOptionText: String,
      selectAllText: String,
    },
    model: {
      prop: 'selected',
      event: 'picklist:changed',
    },
    data() {
      return {
        // eslint-disable-next-line
        uid: this._uid,
        localSelected: this.multiple ? [] : '',
        localDisplayed: '',
        nativeChangeEnabled: false,
        programChangeEnabled: true,
        isDropdownVisible: false,
        isDropdownScrollable: false,
        itemsValues: [],
        isMobile: typeof window !== 'undefined'
          ? (window.matchMedia('(pointer: coarse)').matches
            || /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent))
          : false,
      };
    },
    computed: {
      hasSelected() {
        return this.multiple
          ? Array.isArray(this.localSelected) && this.localSelected.length > 0
          : this.localSelected !== '' && this.localSelected !== null && this.localSelected !== undefined;
      },
      shouldHighlight() { // the highlight flag when user choose something and close the select
        return !this.isDropdownVisible && this.hasSelected;
      },
      safeTheme() { // guarantee a safe value
        const allowed = ['classic', 'pp-modern'];
        return allowed.includes(this.theme) ? this.theme : 'classic';
      },
      themeClass() {
        return `picklist--${this.safeTheme}`;
      },
      filteredItems() {
        let arr = [];
        const index = this.itemsValues.findIndex((
          (i) => i.text === this.localDisplayed
        ));

        if (this.multiple || index !== -1 || this.localDisplayed.length === 0) {
          arr = this.itemsValues;
        } else {
          arr = this.itemsValues.filter((
            (i) => i.text.toLowerCase().includes(this.localDisplayed.toLowerCase())
          ));
        }

        return arr;
      },
      isSelectAll() {
        return this.localSelected && this.localSelected.length === this.itemsValues.length;
      },
    },
    watch: {
      isDropdownVisible(isVisible) {
        if (isVisible) {
          const dropdownHeight = this.$refs.dropdown.clientHeight;
          this.isDropdownScrollable = (dropdownHeight >= 280);
        } else {
          this.isDropdownScrollable = false;
        }
      },
      selected(selected) {
        if (this.programChangeEnabled) {
          this.nativeChangeEnabled = false;
          const selectedOpt = this.getSelectedOpt(selected);
          this.localSelected = selectedOpt.value;
        }
        this.programChangeEnabled = true;
      },
      localSelected(selected) {
        const selectedOpt = this.getSelectedOpt(selected);
        this.localDisplayed = selectedOpt.text;

        if (this.nativeChangeEnabled) {
          this.programChangeEnabled = false;
          this.$emit('picklist:changed', selectedOpt.value);
          this.$emit('change');
        }
        this.nativeChangeEnabled = true;
      },
      items(items) {
        this.itemsValues = items;
      },
      localDisplayed(displayed) {
        // Browser autofill fills the text input without picking an option
        if (this.autocomplete && !this.multiple && displayed) {
          const lowered = displayed.trim().toLowerCase();
          const match = this.itemsValues.find((item) => item.text.toLowerCase() === lowered);

          if (match && match.value !== this.localSelected) {
            this.localSelected = match.value;
          }
        }
      },
    },
    methods: {
      clickOption(option) {
        this.isDropdownVisible = false;

        if (option.value === this.localSelected) {
          this.restoreLocalDisplayed();
        }
      },
      openDropdown() {
        // eslint-disable-next-line no-underscore-dangle
        this.$root.$emit('picklist:close-all', this._uid);
        if (!this.disabled) {
          if (this.autocomplete) {
            this.isDropdownVisible = true;
          } else {
            this.isDropdownVisible = !this.isDropdownVisible;
          }

          // Only if autocomplete ON and there is not a mobile device
          if (this.autocomplete && !this.multiple && !(this.isMobile && !this.autocomplete)) {
            this.$refs.input.select();
          }
        }
      },
      hideDropdown(uid) {
        // eslint-disable-next-line
        if (uid !== this._uid) {
          this.isDropdownVisible = false;
          this.restoreLocalDisplayed();
        }
      },
      getSelectedOpt(selected) {
        let itemValue;
        let itemText = '';

        if (this.multiple) {
          itemValue = [];

          if (Array.isArray(selected)) {
            const filtered = [];

            selected.forEach((item) => {
              const newItem = this.itemsValues.find((
                (v) => v.value === item
              ));
              if (newItem) {
                filtered.push(newItem);
              }
            });

            itemValue = filtered.map((
              (item) => item.value
            ));

            itemText = filtered.map((
              (item) => item.text
            )).join('; ');
          }
        } else {
          itemValue = '';

          const found = this.itemsValues.find((
            (item) => item.value === selected
          ));

          if (found !== undefined) {
            itemValue = found.value;
            itemText = found.text;
          }
        }

        return { value: itemValue, text: itemText };
      },
      restoreLocalDisplayed() {
        if (this.autocomplete && !this.multiple) {
          const filterText = this.items.find((
            (option) => option.text === this.localDisplayed
          ));

          if (filterText === undefined) {
            const selectedOpt = this.items.find((
              (option) => option.value === this.localSelected
            ));

            if (selectedOpt !== undefined) {
              this.localDisplayed = selectedOpt.text;
            } else {
              this.localDisplayed = '';
            }
          }
        }
      },
      isDisabledChekbox(option) {
        return option.disabled
          || (
            this.limit > 0
            && this.localSelected !== undefined
            && this.localSelected.length >= this.limit
            && this.localSelected.indexOf(option.value) === -1
          );
      },
      buildItems(items) {
        return items.map((item) => {
          if (item.children) {
            return this.buildItems(item.children);
          }
          return item;
        });
      },
      bundleIsChecked(option) {
        return this.localSelected.includes(option.value);
      },
      undisabledItems() {
        this.itemsValues = this.itemsValues.map((item) => ({ ...item, disabled: false }));
      },
      toggleBundleItems(option) {
        // if all products of bundle is check, then uncheck
        if (this.bundleIsChecked(option)) {
          this.undisabledItems();
          // uncheck products bundle
          this.localSelected = this.localSelected
            .filter((item) => !option.products.includes(item))
            .filter((item) => option.value !== item);
        } else {
          this.undisabledItems();
          const bundles = this.itemsValues
            .filter((item) => item.type && item.type === 'bundle' && item.value !== option.value)
            .map((item) => item.value);

          // uncheck exclude products
          if (option.exclude) {
            this.localSelected = this.localSelected
              .filter((item) => !option.exclude.includes(item));
          }

          option.products.forEach((product) => {
            // disable products contained bundle
            this.itemsValues = this.itemsValues.map((item) => {
              if (item.value === product) {
                return { ...item, disabled: true };
              }
              return item;
            });

            // check products bundle
            if (!this.localSelected.includes(product)) {
              this.localSelected.push(product);
            }
          });

          this.localSelected = this.localSelected
            .filter((item) => !bundles.includes(item));

          if (!this.localSelected.includes(option.value)) {
            this.localSelected.push(option.value);
          }
        }
      },
      toggleAllOptions() {
        if (this.isSelectAll) {
          this.localSelected = [];
        } else {
          this.localSelected = this.itemsValues.map((item) => item.value);
        }
      },
      changeHandler(option) {
        this.toggleGroupSelection(option.value);
      },
      toggleGroupSelection(selected) {
        if (this.productGroups?.length) {
          const valuesToRemove = [];

          this.productGroups.forEach((group) => {
            if (!group.multipleSelectionAllowed && group.productList.includes(selected)) {
              valuesToRemove.push(...group.productList.filter((product) => product !== selected));
            }
          });

          if (valuesToRemove.length) {
            this.localSelected = this.localSelected.filter(
              (item) => !valuesToRemove.includes(item),
            );
          }
        }
      },
    },
    mounted() {
      this.itemsValues = this.items;
      this.$root.$on('picklist:close-all', this.hideDropdown);

      const selectedOpt = this.getSelectedOpt(this.selected);
      this.localSelected = selectedOpt.value;
    },
    updated() {
      this.nativeChangeEnabled = true;
      this.programChangeEnabled = true;
    },
    beforeDestroy() {
      this.$root.$off('picklist:close-all');
    },
  };
</script>

<style lang="less" scoped>
  @import '../assets/variables';

  // classic theme
  .nwx-picklist {
    position: relative;

    .picklist-cont {
      .picklist-input {
        display: block;
        width: 100%;
        height: 40px;
        padding: 9px 45px 9px 16px;
        font-size: 14px;
        text-overflow: ellipsis;
        background-color: #fff;
        border-radius: 4px;
        border: 1px solid @inputGray;
        transition: border-color ease-out .2s;
        -moz-appearance: none;
        -webkit-appearance: none;
        outline: 0;

        &::placeholder {
          color: fade(@mainGray, 80%);
        }

        &:hover {
          border-color: @labelGray;
        }

        &:focus {
          border-color: @mainBlue !important;
        }

        &::-ms-clear {
          display: none;
        }

        &[disabled],
        &[readonly] {
          opacity: 1;
        }

        &[readonly] {
          cursor: pointer;
        }

        &[disabled] {
          cursor: default;
          color: fade(@mainGray, 80%);
          border-color: fade(@mainGray, 20%) !important;
          background-color: fade(@accessWhite, 60%) !important;

          &::placeholder {
            color: fade(#4D4D4D, 80%) !important;
          }

          &:hover {
            border-color: fade(@mainGray, 20%) !important;
          }
        }
      }

      &:after {
        content: ' ';
        display: block;
        width: 10px;
        height: 6px;
        position: absolute;
        top: 18px;
        right: 19px;
        pointer-events: none;
        background-image: url('//img.netwrix.com/elements/partnerPortal/select_caret_down.svg');
        background-repeat: no-repeat;
        background-position: center;
      }

      &.opened {
        .picklist-input {
          border-radius: 4px 4px 0 0;
          border-color: @mainBlue !important;
          border-bottom-color: transparent !important;
        }

        &:after {
          top: 17px;
          transform: rotate(180deg);
        }
      }

      &.dense {
        .picklist-input {
          height: 30px;
          padding: 4px 45px 4px 16px;
        }

        &:after {
          top: 13px;
        }

        &.opened {
          &:after {
            top: 12px;
          }
        }
      }

      &.focused {
        .picklist-input {
          border-color: @mainBlue;
        }
      }

      &.disabled {
        &:after {
          opacity: 0.4;
        }
      }

      &.error {
        .picklist-input {
          border-color: @mainRed !important;
        }
      }
    }

    .picklist-dropdown {
      min-width: 100%;
      max-height: 282px;
      background: #fff;
      border-radius: 0 0 4px 4px;
      border: 1px solid @inputGray;
      position: absolute;
      top: 39px;
      left: 0;
      pointer-events: none;
      overflow: hidden;
      visibility: hidden;
      opacity: 0;
      z-index: 2;
      transition: visibility 0.2s, opacity 0.2s;

      &.visible {
        border-color: @mainBlue;
        border-top-color: @inputGray;
        pointer-events: auto;
        overflow: initial;
        visibility: visible;
        opacity: 1;
      }

      &.scrollable {
        overflow: auto;
      }

      &.dense {
        top: 29px;
      }

      .no-option-msg {
        height: 40px;
        padding: 10px 16px;
        cursor: default;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }

      .option-row {
        &.disabled-row {
          background: @bodyWhite;

          .checkbox-container {
            color: #aaa;
            &.checked {
              color: #aaa;

              .checkmark {
                border-color: #aaa;
                background-color: #aaa;
              }
            }
          }
        }

        &:nth-last-of-type(1) {
          border-radius: 0 0 4px 4px;
        }

        .checkbox-container,
        .radio-container {
          display: block;
          position: relative;
          cursor: pointer;
          width: 100%;
          //height: 40px;
          margin: 0;
          padding: 10px 21px 10px 16px;
          font-weight: 400;
          font-size: 14px;
          white-space: nowrap;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;

          &:hover {
            background: @bodyWhite;
          }

          &.checked {
            color: @mainBlue;

            .checkmark {
              border-color: @mainBlue;
              background-color: @mainBlue;

              &:after {
                opacity: 1;
              }
            }
          }

          input {
            position: absolute;
            cursor: pointer;
            opacity: 0;
            height: 0;
            width: 0;
          }
        }

        .checkbox-container {
          .label-text {
            padding-left: 26px;
          }

          input {
            &:checked ~ .checkmark {
              border-color: @mainBlue;
              background-color: @mainBlue;
            }

            &:checked ~ .checkmark:after {
              opacity: 1;
            }
          }

          .checkmark {
            position: absolute;
            top: 12px;
            left: 17px;
            height: 16px;
            width: 16px;
            border: 1px solid @inputGray;
            background: #fff;
            border-radius: 2px;
            transition: background 0.2s, border 0.2s;

            &:after {
              content: " ";
              display: block;
              width: 100%;
              height: 100%;
              background: url('//img.netwrix.com/elements/partnerPortal/pipeline/checkmark.svg');
              background-repeat: no-repeat;
              background-position: center;
              opacity: 0;
            }
          }
        }

        .children {
          .checkbox-container {
            padding-left: 40px;

            .checkmark {
              left: 42px;
            }
          }
        }
      }
    }
  }

  // pp-modern theme
  .nwx-picklist .picklist--pp-modern {
    .picklist-cont {

      .picklist-input {
        background: @accessWhite;
        border-radius: 0;
        font-size: 16px;
        border-color: @colorH30;
        &::placeholder {
          color: @colorH100;
        }

        &:hover {
          border-color: @labelGray;
        }

        &:focus {
          color: #000000;
          border-color: #000000 !important;
        }
      }
      &.has-value:not(.opened) .picklist-input {
        border-color: @vigilantBlue !important;
        color: #000000;
      }
      &.opened {
        .picklist-input {
          border-color: #000000 !important;
        }
      }
    }

    .picklist-dropdown {
      background: @accessWhite;
      border-top: none;

      &.visible {
        border-color: #000000;
      }

      .option-row{
        .checkbox-container{

          .label-text {
            color: #424242;
          }

          input {
            &:checked ~ .checkmark {
              border-color: @vigilantBlue;
              background-color: @vigilantBlue;
            }
          }

          &.checked{
            color: @vigilantBlue;
          }
          .checkmark{
            border-color: @vigilantBlue;
            border-radius: 0;
            &::after {
              display: none;
            }

          }
        }
      }
    }
  }
</style>
